import { useState } from 'react'

const SIDEBAR_STATE_KEY = 'sidebar-collapsed'

/**
 * Get initial sidebar state based on localStorage and screen width
 */
function getInitialSidebarState(defaultValue: boolean): boolean {
	// Check if we're on the client side
	if (typeof window === 'undefined') {
		return defaultValue
	}

	try {
		const savedState = localStorage.getItem(SIDEBAR_STATE_KEY)
		if (savedState !== null) {
			const savedCollapsed = JSON.parse(savedState)
			// On mobile devices, always collapse the sidebar regardless of saved state
			const isMobile = window.innerWidth < 768 // md breakpoint
			return isMobile || savedCollapsed
		} else {
			// If no saved state, use responsive default
			const isMobile = window.innerWidth < 768 // md breakpoint
			return isMobile || defaultValue
		}
	} catch (error) {
		console.warn('Failed to load sidebar state from localStorage:', error)
		// Fallback to responsive default
		const isMobile = window.innerWidth < 768 // md breakpoint
		return isMobile || defaultValue
	}
}

/**
 * Custom hook to manage sidebar collapsed state with localStorage persistence and responsive behavior
 * @param defaultValue - Default collapsed state (defaults to false)
 * @returns [isCollapsed, setIsCollapsed] - State and setter function
 */
export function useSidebarState(
	defaultValue: boolean = false
): [boolean, (collapsed: boolean) => void] {
	const [isCollapsed, setIsCollapsed] = useState<boolean>(() => getInitialSidebarState(defaultValue))

	// Save state to localStorage whenever it changes
	const setCollapsedState = (collapsed: boolean) => {
		setIsCollapsed(collapsed)

		// Save to localStorage for all devices to preserve user preference
		try {
			localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify(collapsed))
		} catch (error) {
			console.warn('Failed to save sidebar state to localStorage:', error)
		}
	}

	return [isCollapsed, setCollapsedState]
}
